import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ImageMarquee from '../components/ImageMarquee';

const STATS = [
  { number: '1,200+', label: 'Meals Served Daily' },
  { number: '2', label: 'Distribution Points' },
  { number: '4,500+', label: 'Families Reached' },
  { number: '5', label: 'Months of Service' },
];

const SERVICES = [
  {
    icon: '🍲',
    title: 'Daily Meal Distribution',
    desc: 'Hot, nutritious meals served every day to individuals and families in slums, labor camps, and raw housing colonies.',
  },
  // {
  //   icon: '🤝',
  //   title: 'Community Hunger Relief',
  //   desc: 'We organize food distribution drives in underserved communities to support people struggling with daily food insecurity.',
  // },
  {
    icon: '🤝',
    title: 'Food Campaign Participation',
    desc: 'Members can participate at our quarterly food distribution drives and serve meals, meet families, witness impact.',
  },
  {
    icon: '📍',
    title: 'On-Site Food Drives',
    desc: 'Meals are distributed at selected locations where underprivileged families and daily wage workers gather.',
  },
  {
    icon: '❤️',
    title: 'Humanitarian Support',
    desc: 'Focused efforts to reduce hunger by reaching individuals who are unable to afford regular meals due to financial hardship.',
  },
];

export default function Home() {
  const heroRef = useRef(null);

  useEffect(() => {
    if (heroRef.current) heroRef.current.classList.add('page-enter');
  }, []);

  return (
    <div>
      {/* Hero */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center justify-center overflow-hidden grain-overlay hero-flow-gradient"
        style={{
          background: 'linear-gradient(135deg, #fef9f0 0%, #fef3c7 40%, #fed7aa 100%)',
        }}
      >
        {/* Background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 rounded-full bg-orange-200/30 blur-3xl floating-orb orb-1" />
          <div className="absolute bottom-20 left-10 w-96 h-96 rounded-full bg-amber-200/30 blur-3xl floating-orb orb-2" />
          {/* Geometric rangoli circles */}
          <svg className="absolute top-0 right-0 w-1/3 h-auto opacity-10" viewBox="0 0 400 400">
            <circle cx="200" cy="200" r="180" fill="none" stroke="#f97316" strokeWidth="1" />
            <circle cx="200" cy="200" r="140" fill="none" stroke="#d97706" strokeWidth="1" />
            <circle cx="200" cy="200" r="100" fill="none" stroke="#f97316" strokeWidth="1" />
            {[0,30,60,90,120,150,180,210,240,270,300,330].map(deg => (
              <line
                key={deg}
                x1={200 + 100 * Math.cos(deg * Math.PI/180)}
                y1={200 + 100 * Math.sin(deg * Math.PI/180)}
                x2={200 + 180 * Math.cos(deg * Math.PI/180)}
                y2={200 + 180 * Math.sin(deg * Math.PI/180)}
                stroke="#f97316" strokeWidth="0.5"
              />
            ))}
          </svg>
          <svg className="absolute bottom-0 left-0 w-1/4 h-auto opacity-10" viewBox="0 0 400 400">
            <circle cx="200" cy="200" r="180" fill="none" stroke="#d97706" strokeWidth="1" />
            <circle cx="200" cy="200" r="120" fill="none" stroke="#f97316" strokeWidth="1" />
          </svg>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24">
          {/* Hindi tagline */}
          <div className="font-hindi text-amber-600 text-xl md:text-2xl mb-4 italic">
            अन्नदान महादान — Food is the greatest gift
          </div>

          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl text-amber-900 mb-6 leading-none">
            BSM Bhojan Seva Mandal<br />
            <span className="text-orange-600">Foundation</span>
          </h1>

          <p className="text-xl md:text-2xl text-amber-800 max-w-2xl mx-auto mb-10 font-light leading-relaxed">
            Nourishing lives, one meal at a time — feeding families residing in raw housing and vulnerable shelters across Gujarat.
            Join ₹25/month and get access to our membership benefits.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/membership" className="btn-primary text-base px-10 py-4 btn-shimmer">
              Join Membership ₹25 / Month
            </Link>
            <Link to="/about" className="btn-outline text-base px-10 py-4">
              Our Mission
            </Link>
          </div>

          {/* Trust badge */}
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-amber-700">
            <span className="flex items-center gap-1.5">✅ Registered NGO</span>
            <span className="text-amber-400">|</span>
            <span className="flex items-center gap-1.5">🔒 80G Tax Exemption</span>
            {/* <span className="text-amber-400">|</span>
            <span className="flex items-center gap-1.5">📋 FCRA Compliant</span> */}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-orange-600 py-12">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          {STATS.map(({ number, label }) => (
            <div key={label} className="text-center">
              <span className="font-display text-4xl md:text-5xl text-white block">{number}</span>
              <span className="text-orange-100 text-sm mt-1 block uppercase tracking-wider">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Image Slideshow */}
      <section className="bg-amber-50 py-16">
        <div className="max-w-6xl mx-auto px-6 mb-8">
          <div className="lotus-divider mb-4">🪷 🪷 🪷</div>
          <h2 className="section-title text-center">On the Ground</h2>
          <p className="section-subtitle text-center mx-auto">
            Glimpses from our daily seva operations — kitchens, distribution drives, and communities we serve.
          </p>
        </div>
        <ImageMarquee />
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="lotus-divider mb-4">🪷 🪷 🪷</div>
            <h2 className="section-title">What We Do</h2>
            <p className="section-subtitle mx-auto">
              From hot meals to ration kits — our services are designed to eliminate hunger for the most vulnerable.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map(({ icon, title, desc }) => (
              <div key={title} className="card p-6 glow-card hover:-translate-y-1 transition-transform duration-200">
                <div className="text-4xl mb-4">{icon}</div>
                <h3 className="font-display text-xl text-amber-900 mb-2">{title}</h3>
                <p className="text-amber-700 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="py-20 bg-amber-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, #f97316 0, #f97316 1px, transparent 0, transparent 50%)',
            backgroundSize: '20px 20px',
          }} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl md:text-5xl text-amber-100 mb-6 whitespace-normal lg:whitespace-nowrap">
            {/* Just ₹25/month membership feeds a family for a day. */}
            Become a member at Just ₹25/month.
          </h2>
          <p className="text-amber-300 text-lg mb-8 whitespace-normal md:whitespace-nowrap">
            Set up an automatic monthly membership and become part of our knowledge sharing community.
          </p>
          <Link to="/membership" className="bg-orange-500 text-white font-semibold px-10 py-4 rounded-sm tracking-wider uppercase text-base hover:bg-orange-400 transition-colors inline-block btn-shimmer">
            Start Monthly Membership →
          </Link>
        </div>
      </section>
    </div>
  );
}
